import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addTask,
  deleteTaskById,
  editTask,
  fetchTasks,
} from "../redux/actions/taskAction";
import { Task } from "../types/Task";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import TaskForm from "../components/TaskForm";
import TaskList from "../components/TaskList";
import { StoreState } from "../redux/reducers";
import { useToast } from "../components/toast/ToastProvider";
import { AppDispatch } from "../redux/store";
import { deleteTaskAttachmentAction } from "../redux/actions/taskAttachmentAction";
import { deleteTaskCommentAction } from "../redux/actions/taskCommentAction";
import TaskFilter from "../components/TaskFilter";
import { Project } from "../types/Project";

interface Props {
  taskList?: Task[];
  projectList?: Project[];
}

const TaskPage: React.FC<Props> = ({ taskList, projectList }) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const tasks = useSelector((state: StoreState) => state.task);
  const attachment = useSelector((state: StoreState) => state.taskAttachment);
  const comment = useSelector((state: StoreState) => state.taskComment);

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [viewMode, setViewMode] = useState(false);

  const [filters, setFilters] = useState({
    project: "",
    status: "",
    assignedDev: "",
    assignedQa: "",
    assignedBa: "",
    module: "",
    submodule: "",
    sprint: "",
    page: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  // --- FIX: Default to true so ALL tasks show initially ---
  const [isFiltered, setIsFiltered] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchTasks()).finally(() => setLoading(false));
  }, [dispatch]);

  // Load filters from localStorage on mount (optional persistence)
  useEffect(() => {
    const savedFilters = localStorage.getItem("taskFilters");
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      setFilters(parsedFilters);
      setAppliedFilters(parsedFilters);
      setIsFiltered(true);
    }
    // If no saved filters, isFiltered stays true → shows ALL tasks
  }, []);

  const handleSave = async (task: Task) => {
    setLoading(true);
    try {
      if (selectedTask) {
        await dispatch(editTask(task.id, task) as any);
        toast("Task updated successfully");
      } else {
        await dispatch(addTask(task) as any);
        toast("Task added successfully");
      }
      await dispatch(fetchTasks());
      setSelectedTask(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving task");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (taskId: number) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      setLoading(true);
      try {
        const attachments = attachment.filter(
          (att) => Number(att.taskId) === taskId,
        );
        const comments = comment.filter((comm) => comm.taskId === taskId);

        for (const attachment of attachments) {
          await dispatch(deleteTaskAttachmentAction(attachment.id) as any);
        }

        for (const comment of comments) {
          await dispatch(deleteTaskCommentAction(comment.id) as any);
        }

        await dispatch(deleteTaskById(taskId) as any);

        toast("Task and its attachments deleted successfully");
        await dispatch(fetchTasks());
      } catch (error) {
        toast("Error deleting task or its attachments");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = async (task: Task) => {
    setSelectedTask(task);
    setShowModal(true);
  };

  const handleView = async (task: Task) => {
    setSelectedTask(task);
    setViewMode(true);
    setShowModal(true);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFindFilters = () => {
    setAppliedFilters(filters);
    setIsFiltered(true);
    localStorage.setItem("taskFilters", JSON.stringify(filters));
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      project: "",
      status: "",
      assignedDev: "",
      assignedQa: "",
      assignedBa: "",
      module: "",
      submodule: "",
      sprint: "",
      page: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    // --- FIX: Show ALL tasks after reset ---
    setIsFiltered(true);
    localStorage.removeItem("taskFilters");
  };

  const filteredTasks = useMemo(() => {
    // --- FIX: Always return tasks, filter based on appliedFilters ---
    return tasks.filter((task) => {
      // If no filters applied, show all tasks
      const hasAnyFilter = Object.values(appliedFilters).some(
        (value) => value !== "",
      );

      if (!hasAnyFilter) {
        return true; // Show all tasks
      }

      // Apply individual filters
      const matchesProject =
        !appliedFilters.project ||
        task.projectId?.toString() === appliedFilters.project;
      const matchesStatus =
        !appliedFilters.status || task.status === appliedFilters.status;
      const matchesDev =
        !appliedFilters.assignedDev ||
        task.assignedDev === appliedFilters.assignedDev;
      const matchesQa =
        !appliedFilters.assignedQa ||
        task.assignedQa === appliedFilters.assignedQa;
      const matchesBa =
        !appliedFilters.assignedBa ||
        task.assignedBa === appliedFilters.assignedBa;
      const matchesModule =
        !appliedFilters.module ||
        task.moduleId?.toString() === appliedFilters.module;
      const matchesSubmodule =
        !appliedFilters.submodule ||
        task.subModuleId?.toString() === appliedFilters.submodule;
      const matchesSprint =
        !appliedFilters.sprint ||
        task.sprintId?.toString() === appliedFilters.sprint;
      const matchesPage =
        !appliedFilters.page || task.pageId?.toString() === appliedFilters.page;

      return (
        matchesProject &&
        matchesStatus &&
        matchesDev &&
        matchesQa &&
        matchesBa &&
        matchesModule &&
        matchesSubmodule &&
        matchesSprint &&
        matchesPage
      );
    });
  }, [tasks, appliedFilters]);

  const uniqueValues = (key: keyof Task): string[] =>
    Array.from(
      new Set(tasks.map((task) => task[key]).filter(Boolean)),
    ) as string[];

  return (
    <Main>
      <PageContainer
        title="Task"
        buttonText={loading ? "Loading..." : "ADD"}
        onButtonClick={() => setShowModal(true)}
      >
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelectedTask(null);
            setViewMode(false);
          }}
          headerText={
            viewMode ? "View Task" : selectedTask ? "Update Task" : "Add Task"
          }
        >
          <>
            <TaskForm
              initialData={selectedTask}
              onSave={handleSave}
              clearAfterSubmit={() => {
                setSelectedTask(null);
                setViewMode(false);
              }}
              readOnly={viewMode}
            />

            {viewMode && (
              <div className="d-flex justify-content-end mt-3">
                <button
                  className="btn btn-success"
                  onClick={() => setViewMode(false)}
                >
                  Edit
                </button>
              </div>
            )}
          </>
        </Modal>

        <CollapseCard title="Task Filter">
          <TaskFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFind={handleFindFilters}
            onReset={handleResetFilters}
            statusOptions={uniqueValues("status")}
            devOptions={uniqueValues("assignedDev")}
            qaOptions={uniqueValues("assignedQa")}
            baOptions={uniqueValues("assignedBa")}
            moduleOptions={uniqueValues("module")}
            submoduleOptions={uniqueValues("submodule")}
            pageOptions={uniqueValues("page")}
            sprintOptions={uniqueValues("sprint")}
          />
        </CollapseCard>

        <CollapseCard title="Task List">
          {loading ? (
            <div className="text-center py-3">Loading tasks...</div>
          ) : (
            <>
              <TaskList
                taskList={filteredTasks}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
              {filteredTasks.length === 0 && (
                <div className="text-center py-3 text-muted">
                  No tasks found matching the selected filters.
                </div>
              )}
            </>
          )}
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default TaskPage;